/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { getMinutesAndSeconds } from "@packages/media";
import type { TextProps } from "@packages/ui";
import { constantSemanticColors, Text } from "@packages/ui";
import { useMemo } from "react";

export type TimeProps = Omit<TextProps, "size" | "type"> & {
  timeS: number;
  negative?: boolean;
  size?: "s" | "m" | "l" | "xl";
};

const numberStyle: Interpolation = {
  color: constantSemanticColors.onColorVeryHigh,
  fontFeatureSettings: "tnum",
  fontVariantNumeric: "tabular-nums",
};

const getTextSize = (size: TimeProps["size"]) => {
  switch (size) {
    case "l":
      return "s";
    case "xl":
      return "s";
    case "m":
      return "l";
    case "s":
    default:
      return "m";
  }
};

const getTextType = (size: TimeProps["size"]) => {
  switch (size) {
    case "l":
      return "body";
    case "xl":
      return "display";
    case "s":
    case "m":
    default:
      return "detail";
  }
};

export const Time = ({
  timeS,
  negative = false,
  size = "m",
  ...props
}: TimeProps) => {
  const t = useTranslations();

  const displayTime = useMemo(() => {
    const [minutes, seconds] = getMinutesAndSeconds({ time: timeS });

    if (negative) {
      return t("web_audio_player_time_remaining", {
        "0": minutes,
        "1": seconds,
      });
    }

    return t("web_audio_player_time_elapsed", { "0": minutes, "1": seconds });
  }, [timeS]);

  const textSize = getTextSize(size);
  const textType = getTextType(size);

  return (
    <Text size={textSize} type={textType} css={numberStyle} {...props}>
      {displayTime}
    </Text>
  );
};
